/**
 * ZIP code input handler with localStorage persistence and transit time integration
 */

import { debounce } from "../utils/debounce.js";
import {
  getStoredZipData,
  saveZipData,
  clearZipData,
} from "../services/zipStorage.js";
import { fetchTransitTimes } from "../../transitTimeService.js";
import type {
  ApiResponse,
  DeliveryWindow,
  WarehouseResult,
} from "../../types/apiTypes.js";
import { onTransitDataLoaded, reapplyCurrentFilter } from "../components/availabilityFilter.js";
import { formatPickupReady } from "../utils/pickupReady.js";

const DEFAULT_ZIP = "70806";

/**
 * Get the customer's default ZIP code from the input's data attribute
 * @returns The default ZIP code
 */
function getCustomerDefaultZip(): string {
  const $zipInput = $("#shipToZip");
  if ($zipInput.length > 0) {
    const defaultZip = $zipInput.data("default-zip");
    if (defaultZip && /^\d{5}$/.test(String(defaultZip))) {
      return String(defaultZip);
    }
  }
  return DEFAULT_ZIP;
}

/**
 * Update the visual state of the ZIP input container
 * Adds red border when ZIP differs from customer's default
 */
function updateZipVisualState(): void {
  const $zipInput = $("#shipToZip");
  const $wrapper = $zipInput.closest(".ship-to-zip-wrapper");

  if ($zipInput.length === 0 || $wrapper.length === 0) return;

  const currentZip = ($zipInput.val() as string).trim();
  const defaultZip = getCustomerDefaultZip();

  if (currentZip !== defaultZip && /^\d{5}$/.test(currentZip)) {
    $wrapper.addClass("zip-modified");
  } else {
    $wrapper.removeClass("zip-modified");
  }
}

/**
 * Reset the Ship To ZIP input back to the customer's default ZIP.
 * Updates the value, clears persisted storage, updates the visual
 * (red-border) state, and triggers a transit-time reload.
 */
export function resetZipToDefault(): void {
  const $zipInput = $("#shipToZip");
  if ($zipInput.length === 0) return;

  const defaultZip = getCustomerDefaultZip();
  $zipInput.val(defaultZip);
  clearZipData();
  updateZipVisualState();

  $(".transit-info").html('<span class="transit-loading">Loading...</span>');
  loadTransitTimes();
}

/**
 * Get ZIP code from input or fallback to default
 * @returns ZIP code
 */
export function getShipToZip(): string {
  const $zipInput = $("#shipToZip");
  if ($zipInput.length > 0) {
    const zip = ($zipInput.val() as string).trim();
    if (/^\d{5}$/.test(zip)) {
      return zip;
    }
  }

  const storedData = getStoredZipData();
  if (storedData) {
    return storedData.zip;
  }

  return getCustomerDefaultZip();
}

/**
 * Collect warehouse codes from all warehouse rows on the page
 * @returns Array of warehouse codes
 */
function collectWarehouseCodes(): string[] {
  const codes: string[] = [];
  $(".warehouse_row").each(function () {
    const code = $(this).attr("data-warehouse-code");
    if (code && !codes.includes(code)) {
      codes.push(code);
    }
  });
  return codes;
}

/**
 * Format a date as "today" / "tomorrow", else "Ddd, Mon D".
 *
 * The inline estimate previously always showed the UPS ship date, so a warehouse that could be
 * picked up or locally delivered TOMORROW still advertised the ship ETA two days out.
 */
function formatRelativeDayLabel(value: Date): string {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const dayOnly = new Date(value.getFullYear(), value.getMonth(), value.getDate());
  if (dayOnly.getTime() === today.getTime()) return "today";
  if (dayOnly.getTime() === tomorrow.getTime()) return "tomorrow";

  return value.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/**
 * Render a local delivery window as a suffix, e.g. ", 8 AM–12 PM".
 *
 * Returns an empty string when the server sent no window — either because it predates the priced
 * quotes, or because a mixed cart had no date to anchor a slot to.
 *
 * @param window - localDelivery.deliveryWindow, if present
 * @returns Suffix to append to the delivery line, or "" to omit it
 */
function formatDeliveryWindow(window: DeliveryWindow | null | undefined): string {
  if (window === "morning") return ", 8 AM–12 PM";
  if (window === "afternoon") return ", 12–4 PM";
  return "";
}

/**
 * Update transit time display for all warehouse rows.
 *
 * Takes the whole response, not just `results`: local delivery and pickup readiness are top-level
 * facts while the ship ETA is per warehouse. Shows every fact that applies rather than the ship
 * date alone.
 *
 * @param response - Full API response
 */
export function updateTransitTimeDisplay(
  response: ApiResponse,
  localOptions: ApiResponse = response,
): void {
  const results: WarehouseResult[] = response.results;
  // Delivery/pickup come from the SCOPED response (see fetchLocalOptions) -- the page-wide one
  // reports order-level dates dragged out to the slowest warehouse on the page.
  const { enableLocalDelivery, nextDeliveryDate, pickupDateTime } = localOptions;
  // Optional on the type: the server may be running a build that predates the priced quotes, and
  // the theme can deploy ahead of it. Guard, never assume.
  const deliveryWindowText = formatDeliveryWindow(
    localOptions.localDelivery?.deliveryWindow,
  );

  results.forEach((result) => {
    const $row = $(
      `.warehouse_row[data-warehouse-code="${result.warehouseCode}"]`,
    );
    if ($row.length === 0) return;

    const $transitInfo = $row.find(".transit-info");
    if ($transitInfo.length === 0) return;

    if (result.totalDays === null) {
      $transitInfo.html('<span class="transit-unavailable">N/A</span>');
      return;
    }

    // Store order-by and delivery details for the (i) modal
    const orderByFormatted = result.orderByDT
      ? formatOrderByDateTime(result.orderByDT)
      : "";
    const deliveryFormatted = result.estimatedDeliveryDate
      ? formatDeliveryDate(result.estimatedDeliveryDate)
      : "";

    if (result.estimatedDeliveryDate) {
      // Set data attribute for availability filtering
      $row.attr("data-estimated-delivery", result.estimatedDeliveryDate);
    }

    // `totalDays` is CALENDAR days; `totalBusinessDays` is the business-day count. This line read
    // totalDays while saying "business days", which overstated the wait by two days over any
    // weekend. Fall back to totalDays only if the server omitted the business count.
    const businessDays = result.totalBusinessDays ?? result.totalDays;
    const daysText = `${businessDays} business day${businessDays !== 1 ? "s" : ""}`;

    // One row per fulfilment method, each omitted entirely when it does not apply. Local delivery
    // and pickup are only offered from a warehouse we stock ourselves, which is exactly the one the
    // API marks pickupAvailable -- keyed off the data, not a hardcoded 'WPBR'.
    const rows: string[] = [];
    if (result.pickupAvailable) {
      if (enableLocalDelivery && nextDeliveryDate) {
        const deliveryOn = new Date(`${nextDeliveryDate}T00:00:00`);
        if (!isNaN(deliveryOn.getTime())) {
          // "Delivery today, 12–4 PM" once the server supplies a window. It can genuinely be
          // never today — one morning run a day, always the next business day (2026-08-31).
          rows.push(
            `<div class="transit-sooner">Delivery ${formatRelativeDayLabel(deliveryOn)}${deliveryWindowText}</div>`,
          );
        }
      }
      if (pickupDateTime) {
        const ready = formatPickupReady(pickupDateTime);
        if (ready) {
          rows.push(`<div class="transit-sooner">Pickup ${ready}</div>`);
        }
      }
    }

    // Show estimated delivery date inline if available, otherwise fall back to business days
    if (result.estimatedDeliveryDate) {
      const deliveryShort = formatDeliveryDateShort(result.estimatedDeliveryDate);
      // "Ship:" not "Est:" -- with delivery/pickup rows above it, an unlabelled date is ambiguous,
      // and that ambiguity is what made this confusing in the first place.
      rows.push(`<div class="transit-days">Ship: ${deliveryShort}</div>`);
    } else {
      rows.push(`<div class="transit-days">${daysText}</div>`);
    }
    $transitInfo.html(rows.join(""));

    // Store modal data after setting html (jQuery .data() persists on the element itself)
    $transitInfo.data("transit-order-by", orderByFormatted);
    $transitInfo.data("transit-delivery", deliveryFormatted);
    $transitInfo.data("transit-days-text", daysText);

    // Set tooltip text on the (i) button for mouse-over convenience
    const tooltipLines: string[] = [];
    if (daysText) tooltipLines.push(daysText);
    if (orderByFormatted) tooltipLines.push(`Order by: ${orderByFormatted}`);
    if (deliveryFormatted) tooltipLines.push(`Est. Delivery: ${deliveryFormatted}`);
    if (tooltipLines.length > 0) {
      $row.find(".warehouse-info-btn").attr("data-tooltip", tooltipLines.join("\n"));
    }
  });
}

/**
 * Show fallback message when transit times fail to load
 */
function showTransitTimeFallback(): void {
  $(".transit-info").html('<span class="transit-unavailable">N/A</span>');
}

/**
 * Format delivery date string
 * @param dateString - ISO date string
 * @returns Formatted date
 */
function formatDeliveryDate(dateString: string): string {
  try {
    const date = new Date(dateString + "T00:00:00");
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  } catch (e) {
    return dateString;
  }
}

/**
 * Format delivery date as short string: "Wed, Mar 11"
 */
function formatDeliveryDateShort(dateString: string): string {
  try {
    const date = new Date(dateString + "T00:00:00");
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  } catch (e) {
    return dateString;
  }
}

/**
 * Format order by date/time string
 * @param orderByDT - ISO datetime string
 * @returns Formatted datetime
 */
function formatOrderByDateTime(orderByDT: string): string {
  try {
    const date = new Date(orderByDT);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    let dayPrefix = "";
    if (date.toDateString() === today.toDateString()) {
      dayPrefix = "Today ";
    } else if (date.toDateString() === tomorrow.toDateString()) {
      dayPrefix = "Tomorrow ";
    }

    const time = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    return `${dayPrefix}${time}`;
  } catch (e) {
    return orderByDT;
  }
}

/**
 * Resolve local delivery / pickup dates that are actually true for the stocking warehouse.
 *
 * `nextDeliveryDate` and `pickupDateTime` are ORDER-level: the server computes them across the
 * whole set of warehouses in the request (see calculateNextDeliveryDate / calculatePickupDateTime),
 * answering "when could an order containing all of these be delivered or collected". That is right
 * for the cart, where every item must be gathered before anything moves.
 *
 * The product page asks about EVERY warehouse on the page at once, so those order-level dates get
 * dragged out to the slowest one -- on 2026-08-02 the page showed "Delivery Wed, Aug 12" for a
 * warehouse the cart correctly reported as deliverable TOMORROW. Each row here is an independent
 * option, so the honest answer is what that warehouse alone can do.
 *
 * Costs one extra request, only when the page mixes stocking and non-stocking warehouses, and the
 * server caches transit lookups. On failure we fall back to the page-wide response rather than
 * failing the render -- a slightly pessimistic date beats no estimates at all.
 *
 * ⚠ EXCEPT the pickup time. The page-wide `pickupDateTime` is the REMOTE-goods rule (10:00 AM a
 * business day after the slowest warehouse arrives), so showing it on a WPBR row would promise
 * "Pickup Mon by 10:00 AM" for goods that are on the shelf. A wrong pickup time is worse than none,
 * so the fallback carries no pickup time at all.
 */
async function fetchLocalOptions(
  pageResponse: ApiResponse,
  destZip: string,
): Promise<ApiResponse> {
  const pickupCodes = pageResponse.results
    .filter((result) => result.pickupAvailable)
    .map((result) => result.warehouseCode);

  // Nothing stocking on the page, or the page is ALREADY scoped to just those warehouses.
  if (pickupCodes.length === 0 || pickupCodes.length === pageResponse.results.length) {
    return pageResponse;
  }

  try {
    return await fetchTransitTimes(pickupCodes, destZip);
  } catch (error) {
    console.warn("Scoped local-options lookup failed; using page-wide dates without a pickup time", error);
    return { ...pageResponse, pickupDateTime: null };
  }
}

/**
 * Fetch and update transit times for all warehouses on the page
 */
export async function loadTransitTimes(): Promise<void> {
  const warehouseCodes = collectWarehouseCodes();

  if (warehouseCodes.length === 0) {
    if ($(".warehouse_row").length > 0) {
      console.warn(
        "Warehouse rows found but no warehouse codes - check data-warehouse-code attributes",
      );
    }
    return;
  }

  const destZip = getShipToZip();

  try {
    $(".transit-info").html('<span class="transit-loading">Loading...</span>');

    const response: ApiResponse = await fetchTransitTimes(warehouseCodes, destZip);

    if (response && response.results) {
      updateTransitTimeDisplay(response, await fetchLocalOptions(response, destZip));
      onTransitDataLoaded(response.results);
    } else {
      showTransitTimeFallback();
    }
  } catch (error) {
    console.error("❌ Transit time fetch failed:", error);
    showTransitTimeFallback();
  } finally {
    // Always re-run the active availability filter after transit load resolves.
    // Why: lazy-loaded rows inserted before transit returned may be missing
    // data-estimated-delivery (reference-row copy in onProductLoaded can fail
    // when no sibling row has finished transit loading yet). Even in the
    // fallback/error paths we must re-apply so those rows get re-evaluated
    // against the now-permissive "5days" rule (see applyFilter in
    // availabilityFilter.ts) instead of staying invisible forever.
    reapplyCurrentFilter();
  }
}

/**
 * Initialize ZIP code input handler and event listeners
 */
export function initZipCodeHandler(): void {
  const $zipInput = $("#shipToZip");

  if ($zipInput.length > 0) {
    const storedData = getStoredZipData();
    if (storedData) {
      $zipInput.val(storedData.zip);
      saveZipData(storedData.zip);
    }
    updateZipVisualState();
  }

  $(document).on("keypress", "#shipToZip", function (e: JQuery.KeyPressEvent) {
    if (
      [8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
      (e.keyCode >= 35 && e.keyCode <= 40)
    ) {
      return;
    }
    if (
      (e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
      (e.keyCode < 96 || e.keyCode > 105)
    ) {
      e.preventDefault();
    }
  });

  $(document).on("paste", "#shipToZip", function () {
    const $input = $(this);
    setTimeout(function () {
      const cleaned = ($input.val() as string).replace(/\D/g, "").substring(0, 5);
      $input.val(cleaned);
    }, 0);
  });

  const debouncedLoadTransitTimes = debounce(function () {
    const $zipInput = $("#shipToZip");
    let zip = ($zipInput.val() as string).trim();

    zip = zip.replace(/\D/g, "");
    $zipInput.val(zip);

    if (zip === "") {
      const defaultZip = getCustomerDefaultZip();
      $zipInput.val(defaultZip);
      clearZipData();
      updateZipVisualState();
      $(".transit-info").html(
        '<span class="transit-loading">Loading...</span>',
      );
      loadTransitTimes();
    } else if (/^\d{5}$/.test(zip)) {
      saveZipData(zip);
      updateZipVisualState();
      $(".transit-info").html(
        '<span class="transit-loading">Loading...</span>',
      );
      loadTransitTimes();
    }
  }, 500);

  $(document).on("input", "#shipToZip", debouncedLoadTransitTimes);
}
